import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { ArrowLeft, Plus, FlaskConical, BadgeCheck, Pencil, Trash2 } from "lucide-react";
import { api } from "@/lib/api";

interface LaboratorioDetalle {
  id: string;
  razonSocial: string;
  nombreCorto: string | null;
  rfc: string | null;
  acreditacionEma: string | null;
  acreditacionVigenciaInicio: string | null;
  acreditacionVigenciaFin: string | null;
  autorizacionIft: string | null;
  autorizacionIftFecha: string | null;
  contactoCorreo: string | null;
  contactoTelefono: string | null;
  pais: string;
  estatus: string;
  notas: string | null;
  normasAsignadas: number;
}

interface NormaPar {
  id: string;
  normaId: string;
  normaCodigo: string;
  normaTitulo: string;
  alcance: string;
  vigenciaInicio: string;
  vigenciaFin: string | null;
  estatus: string;
  notas: string | null;
  vigente: boolean;
}

interface Acreditacion {
  id: string;
  laboratorioId: string;
  tipo: string;
  identificador: string;
  fechaEmision: string | null;
  vigenciaFin: string | null;
  estatus: string;
  notas: string | null;
  /** En cuántos certificados ya emitidos se usó este número. */
  usosEnCertificados: number;
}

/** EMA acredita; DGN e IFT autorizan; CONUEE aprueba. Así se imprime en el certificado. */
const TIPOS_ACREDITACION = [
  { valor: "EMA",    etiqueta: "EMA — Acreditación" },
  { valor: "IFT",    etiqueta: "IFT — Autorización" },
  { valor: "DGN",    etiqueta: "DGN — Autorización" },
  { valor: "CONUEE", etiqueta: "CONUEE — Aprobación" },
];

interface NormaOpcion {
  id: string;
  codigo: string;
  titulo: string;
  estatus: string;
}

function badgeColor(estatus: string): string {
  switch (estatus) {
    case "ACTIVO":      return "bg-estado-ok-bg text-estado-ok";
    case "SUSPENDIDO":  return "bg-estado-warn-bg text-estado-warn";
    case "INACTIVO":    return "bg-estado-block-bg text-estado-block";
    default:            return "bg-estado-block-bg text-estado-block";
  }
}

export function LaboratorioDetailPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [showAsignar, setShowAsignar] = useState(false);
  const [acrEnEdicion, setAcrEnEdicion] = useState<Acreditacion | null>(null);
  const [showAltaAcr, setShowAltaAcr] = useState(false);

  const lab = useQuery({
    queryKey: ["laboratorio", id],
    queryFn: async () => (await api.get<LaboratorioDetalle>(`/api/catalogos/laboratorios/${id}`)).data,
    enabled: !!id,
  });

  const normas = useQuery({
    queryKey: ["laboratorio", id, "normas"],
    queryFn: async () => (await api.get<NormaPar[]>(`/api/catalogos/laboratorios/${id}/normas`)).data,
    enabled: !!id,
  });

  const acreditaciones = useQuery({
    queryKey: ["laboratorio", id, "acreditaciones"],
    queryFn: async () =>
      (await api.get<Acreditacion[]>(`/api/catalogos/laboratorios/${id}/acreditaciones`)).data,
    enabled: !!id,
  });

  const eliminarAcr = useMutation({
    mutationFn: async (acr: Acreditacion) => {
      const aviso = acr.usosEnCertificados > 0
        ? `Este número se usó en ${acr.usosEnCertificados} certificado(s) emitido(s), así que no se borra: se marcará REVOCADA y dejará de ofrecerse en altas nuevas.\n\nMotivo:`
        : "Motivo para eliminar este número:";
      const motivo = window.prompt(aviso);
      if (!motivo) throw new Error("Cancelado");
      await api.delete(
        `/api/catalogos/laboratorios/${id}/acreditaciones/${acr.id}?motivo=${encodeURIComponent(motivo)}`,
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["laboratorio", id, "acreditaciones"] }),
  });

  const cambiarEstatusLab = useMutation({
    mutationFn: async (accion: "suspender" | "reactivar" | "desactivar") => {
      const motivo = window.prompt(`Motivo para ${accion} este laboratorio:`);
      if (!motivo) throw new Error("Cancelado");
      await api.post(`/api/catalogos/laboratorios/${id}/${accion}`, { motivo });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["laboratorio", id] });
      qc.invalidateQueries({ queryKey: ["laboratorios"] });
    },
  });

  const cambiarEstatusPar = useMutation({
    mutationFn: async ({ parId, accion }: { parId: string; accion: "suspender" | "reactivar" | "desactivar" }) => {
      const motivo = window.prompt(`Motivo para ${accion} esta norma:`);
      if (!motivo) throw new Error("Cancelado");
      await api.post(`/api/catalogos/norma-laboratorio/${parId}/${accion}`, { motivo });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["laboratorio", id, "normas"] });
      qc.invalidateQueries({ queryKey: ["laboratorio", id] });
    },
  });

  if (lab.isLoading) return <p className="text-sm text-fg-subtle">Cargando…</p>;
  if (lab.error || !lab.data) return <p className="text-sm text-estado-err">Laboratorio no encontrado.</p>;

  const l = lab.data;

  return (
    <div className="space-y-4">
      <Link
        to="/admin/laboratorios"
        className="inline-flex items-center gap-1 text-sm text-fg-muted hover:underline"
      >
        <ArrowLeft className="h-4 w-4" /> Volver al listado
      </Link>

      <header className="rounded-lg border bg-surface p-4 shadow-card">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-fg">{l.razonSocial}</h1>
            {l.nombreCorto && <p className="text-sm text-fg-muted">({l.nombreCorto})</p>}
            <p className="mt-2 text-sm text-fg-muted">
              RFC <span className="font-mono">{l.rfc ?? "—"}</span> · País {l.pais}
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <span className={`rounded px-2 py-0.5 text-xs font-medium ${badgeColor(l.estatus)}`}>
              {l.estatus}
            </span>
            <div className="flex gap-1">
              {l.estatus === "ACTIVO" && (
                <button
                  onClick={() => cambiarEstatusLab.mutate("suspender")}
                  className="cursor-pointer rounded border bg-surface px-2 py-1 text-xs transition hover:bg-estado-warn-bg"
                >
                  Suspender
                </button>
              )}
              {l.estatus === "SUSPENDIDO" && (
                <button
                  onClick={() => cambiarEstatusLab.mutate("reactivar")}
                  className="cursor-pointer rounded border bg-surface px-2 py-1 text-xs transition hover:bg-estado-ok-bg"
                >
                  Reactivar
                </button>
              )}
              {l.estatus !== "INACTIVO" && (
                <button
                  onClick={() => cambiarEstatusLab.mutate("desactivar")}
                  className="cursor-pointer rounded border bg-surface px-2 py-1 text-xs text-estado-err transition hover:bg-estado-err-bg"
                >
                  Desactivar
                </button>
              )}
            </div>
          </div>
        </div>

        <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
          <Pair label="Acreditación EMA" value={l.acreditacionEma} />
          <Pair
            label="Vigencia EMA"
            value={
              l.acreditacionVigenciaInicio || l.acreditacionVigenciaFin
                ? `${l.acreditacionVigenciaInicio ?? "—"}  →  ${l.acreditacionVigenciaFin ?? "—"}`
                : null
            }
          />
          <Pair label="Autorización IFT" value={l.autorizacionIft} />
          <Pair label="Fecha IFT" value={l.autorizacionIftFecha} />
          <Pair label="Contacto correo" value={l.contactoCorreo} />
          <Pair label="Contacto teléfono" value={l.contactoTelefono} />
        </dl>
        {l.notas && (
          <p className="mt-4 rounded bg-surface-2 p-3 text-sm text-fg-muted">
            <span className="font-medium">Notas: </span>{l.notas}
          </p>
        )}
      </header>

      <section className="rounded-lg border bg-surface p-4 shadow-card">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-fg">
            <BadgeCheck className="mr-1 inline h-4 w-4" />
            Números de acreditación y aprobación
          </h2>
          <button
            onClick={() => setShowAltaAcr(true)}
            className="inline-flex cursor-pointer items-center gap-1 rounded bg-primary px-2.5 py-1.5 text-sm text-white transition hover:bg-primary-hover"
          >
            <Plus className="h-4 w-4" /> Agregar número
          </button>
        </div>

        <p className="mb-3 rounded bg-surface-2 p-2.5 text-xs text-fg-muted">
          Estos números se imprimen en el Certificado de Conformidad Único. Los certificados
          ya emitidos conservan el número con el que salieron, así que corregir aquí
          <span className="font-medium"> sólo aplica a los certificados que se emitan de aquí en adelante</span>.
        </p>

        {acreditaciones.isLoading && <p className="text-sm text-fg-subtle">Cargando…</p>}
        {acreditaciones.data && acreditaciones.data.length === 0 && (
          <p className="rounded border border-dashed p-4 text-center text-sm text-fg-subtle">
            Sin números registrados. Mientras no haya ninguno, este laboratorio no podrá
            seleccionarse al emitir un certificado.
          </p>
        )}
        {acreditaciones.data && acreditaciones.data.length > 0 && (
          <div className="w-full overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-surface-2 text-left text-xs uppercase tracking-wide text-fg-muted">
                <tr>
                  <th className="px-3 py-2">Organismo</th>
                  <th className="px-3 py-2">Número</th>
                  <th className="px-3 py-2">Emisión</th>
                  <th className="px-3 py-2">Vigencia</th>
                  <th className="px-3 py-2">Estatus</th>
                  <th className="px-3 py-2">Usos</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {acreditaciones.data.map((a) => (
                  <tr key={a.id} className="align-top">
                    <td className="px-3 py-2 font-medium text-fg">{a.tipo}</td>
                    <td className="px-3 py-2 font-mono text-fg">{a.identificador}</td>
                    <td className="px-3 py-2 text-fg-muted">{a.fechaEmision ?? "—"}</td>
                    <td className="px-3 py-2 text-fg-muted">{a.vigenciaFin ?? "Sin vencimiento"}</td>
                    <td className="px-3 py-2">
                      <span className={`rounded px-2 py-0.5 text-xs font-medium ${
                        a.estatus === "VIGENTE" ? "bg-estado-ok-bg text-estado-ok" : "bg-estado-block-bg text-estado-block"
                      }`}>
                        {a.estatus}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-fg-muted">
                      {a.usosEnCertificados > 0
                        ? <span title="Certificados emitidos que lo usaron. Conservan su número original.">
                            {a.usosEnCertificados}
                          </span>
                        : "—"}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => setAcrEnEdicion(a)}
                          title="Editar número"
                          className="cursor-pointer rounded border bg-surface p-1 transition hover:bg-surface-2"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => eliminarAcr.mutate(a)}
                          title={a.usosEnCertificados > 0 ? "Revocar (se usó en certificados)" : "Eliminar"}
                          className="cursor-pointer rounded border bg-surface p-1 text-estado-err transition hover:bg-estado-err-bg"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="rounded-lg border bg-surface p-4 shadow-card">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-fg">
            <FlaskConical className="mr-1 inline h-4 w-4" />
            Normas asignadas
          </h2>
          <button
            onClick={() => setShowAsignar(true)}
            className="inline-flex cursor-pointer items-center gap-1 rounded bg-primary px-2.5 py-1.5 text-sm text-white transition hover:bg-primary-hover"
          >
            <Plus className="h-4 w-4" /> Asignar norma
          </button>
        </div>

        {normas.isLoading && <p className="text-sm text-fg-subtle">Cargando…</p>}
        {normas.data && normas.data.length === 0 && (
          <p className="rounded border border-dashed p-4 text-center text-sm text-fg-subtle">
            No hay normas asignadas todavía.
          </p>
        )}
        {normas.data && normas.data.length > 0 && (
          <div className="w-full overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-surface-2 text-left text-xs uppercase tracking-wide text-fg-muted">
              <tr>
                <th className="px-3 py-2">Norma</th>
                <th className="px-3 py-2">Alcance</th>
                <th className="px-3 py-2">Vigencia</th>
                <th className="px-3 py-2">Estatus</th>
                <th className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {normas.data.map((p) => (
                <tr key={p.id} className="align-top">
                  <td className="px-3 py-2">
                    <div className="font-medium text-fg">{p.normaCodigo}</div>
                    <div className="text-xs text-fg-subtle">{p.normaTitulo}</div>
                  </td>
                  <td className="px-3 py-2 text-fg-muted">{p.alcance}</td>
                  <td className="px-3 py-2 text-fg-muted">
                    {p.vigenciaInicio} → {p.vigenciaFin ?? "—"}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`rounded px-2 py-0.5 text-xs font-medium ${badgeColor(p.estatus)}`}>
                      {p.estatus}
                    </span>
                    {!p.vigente && p.estatus === "ACTIVO" && (
                      <div className="mt-1 text-xs text-estado-warn">Fuera de vigencia</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {p.estatus === "ACTIVO" && (
                      <button
                        onClick={() => cambiarEstatusPar.mutate({ parId: p.id, accion: "suspender" })}
                        className="cursor-pointer rounded border bg-surface px-2 py-0.5 text-xs transition hover:bg-estado-warn-bg"
                      >
                        Suspender
                      </button>
                    )}
                    {p.estatus === "SUSPENDIDO" && (
                      <button
                        onClick={() => cambiarEstatusPar.mutate({ parId: p.id, accion: "reactivar" })}
                        className="cursor-pointer rounded border bg-surface px-2 py-0.5 text-xs transition hover:bg-estado-ok-bg"
                      >
                        Reactivar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </section>

      {showAsignar && (
        <AsignarNormaModal
          laboratorioId={id!}
          onClose={() => setShowAsignar(false)}
          onSuccess={() => {
            setShowAsignar(false);
            qc.invalidateQueries({ queryKey: ["laboratorio", id, "normas"] });
            qc.invalidateQueries({ queryKey: ["laboratorio", id] });
          }}
        />
      )}

      {(showAltaAcr || acrEnEdicion) && (
        <AcreditacionModal
          laboratorioId={id!}
          acreditacion={acrEnEdicion}
          onClose={() => { setShowAltaAcr(false); setAcrEnEdicion(null); }}
          onSuccess={() => {
            setShowAltaAcr(false);
            setAcrEnEdicion(null);
            qc.invalidateQueries({ queryKey: ["laboratorio", id, "acreditaciones"] });
          }}
        />
      )}
    </div>
  );
}

function Pair({ label, value }: { label: string; value: string | null }) {
  return (
    <div className="flex gap-2">
      <dt className="min-w-[140px] text-fg-subtle">{label}</dt>
      <dd className="text-fg">{value ?? <span className="text-fg-faint">—</span>}</dd>
    </div>
  );
}

function AsignarNormaModal({
  laboratorioId,
  onClose,
  onSuccess,
}: {
  laboratorioId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [normaId, setNormaId] = useState("");
  const [alcance, setAlcance] = useState("");
  const [vigenciaInicio, setVigenciaInicio] = useState("");
  const [vigenciaFin, setVigenciaFin] = useState("");
  const [notas, setNotas] = useState("");
  const [error, setError] = useState<string | null>(null);

  const normas = useQuery({
    queryKey: ["normas-catalogo"],
    queryFn: async () =>
      (await api.get<NormaOpcion[]>("/api/catalogos/normas?activas=true")).data,
  });

  const asignar = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { normaId, alcance };
      if (vigenciaInicio) body.vigenciaInicio = vigenciaInicio;
      if (vigenciaFin) body.vigenciaFin = vigenciaFin;
      if (notas) body.notas = notas;
      await api.post(`/api/catalogos/laboratorios/${laboratorioId}/normas`, body);
    },
    onSuccess,
    onError: (err: AxiosError<{ detail?: string }>) => {
      setError(err.response?.data?.detail ?? "Error al asignar norma");
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg space-y-3 rounded-lg bg-surface p-4 shadow-overlay">
        <h3 className="text-lg font-semibold text-fg">Asignar norma</h3>
        <p className="text-sm text-fg-muted">
          Vincula este laboratorio con una NOM/DT activa, con alcance y vigencia propios del par.
        </p>

        <label className="block text-sm">
          <span className="font-medium text-fg-muted">NOM/DT *</span>
          <select
            value={normaId}
            onChange={(e) => setNormaId(e.target.value)}
            className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
          >
            <option value="">Selecciona…</option>
            {(normas.data ?? []).map((n) => (
              <option key={n.id} value={n.id}>
                {n.codigo} — {n.titulo}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm">
          <span className="font-medium text-fg-muted">Alcance *</span>
          <textarea
            rows={3}
            value={alcance}
            onChange={(e) => setAlcance(e.target.value)}
            placeholder="Describir el alcance acreditado para esta norma"
            className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
          />
        </label>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <label className="block text-sm">
            <span className="font-medium text-fg-muted">Vigencia inicio</span>
            <input
              type="date"
              value={vigenciaInicio}
              onChange={(e) => setVigenciaInicio(e.target.value)}
              className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
            />
          </label>
          <label className="block text-sm">
            <span className="font-medium text-fg-muted">Vigencia fin</span>
            <input
              type="date"
              value={vigenciaFin}
              onChange={(e) => setVigenciaFin(e.target.value)}
              className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
            />
          </label>
        </div>

        <label className="block text-sm">
          <span className="font-medium text-fg-muted">Notas</span>
          <textarea
            rows={2}
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
          />
        </label>

        {error && (
          <div className="rounded border border-estado-err/25 bg-estado-err-bg p-2 text-sm text-estado-err">{error}</div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="cursor-pointer rounded border bg-surface px-3 py-1.5 text-sm transition hover:bg-surface-2"
          >
            Cancelar
          </button>
          <button
            disabled={!normaId || !alcance.trim() || asignar.isPending}
            onClick={() => {
              setError(null);
              asignar.mutate();
            }}
            className="cursor-pointer rounded bg-primary px-3 py-1.5 text-sm font-medium text-white transition hover:bg-primary-hover disabled:opacity-50"
          >
            Asignar
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Alta y edición del número de acreditación/autorización/aprobación.
 *
 * El layout sigue el patrón de UploadDocumentoModal (cabecera y pie fijos,
 * cuerpo desplazable): sin él, en pantallas cortas el modal crecía y el botón
 * de guardar quedaba fuera de la vista.
 */
function AcreditacionModal({
  laboratorioId,
  acreditacion,
  onClose,
  onSuccess,
}: {
  laboratorioId: string;
  acreditacion: Acreditacion | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const esEdicion = acreditacion !== null;
  const [tipo, setTipo] = useState(acreditacion?.tipo ?? "");
  const [identificador, setIdentificador] = useState(acreditacion?.identificador ?? "");
  const [fechaEmision, setFechaEmision] = useState(acreditacion?.fechaEmision ?? "");
  const [vigenciaFin, setVigenciaFin] = useState(acreditacion?.vigenciaFin ?? "");
  const [estatus, setEstatus] = useState(acreditacion?.estatus ?? "VIGENTE");
  const [notas, setNotas] = useState(acreditacion?.notas ?? "");
  const [motivoCambio, setMotivoCambio] = useState("");
  const [error, setError] = useState<string | null>(null);

  const guardar = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { tipo, identificador: identificador.trim(), estatus };
      body.fechaEmision = fechaEmision || null;
      body.vigenciaFin = vigenciaFin || null;
      body.notas = notas.trim() || null;
      body.motivoCambio = motivoCambio.trim() || null;
      if (esEdicion) {
        await api.put(
          `/api/catalogos/laboratorios/${laboratorioId}/acreditaciones/${acreditacion!.id}`, body);
      } else {
        await api.post(`/api/catalogos/laboratorios/${laboratorioId}/acreditaciones`, body);
      }
    },
    onSuccess,
    onError: (err: AxiosError<{ detail?: string }>) => {
      setError(err.response?.data?.detail ?? "No se pudo guardar el número");
    },
  });

  const usos = acreditacion?.usosEnCertificados ?? 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-lg bg-surface shadow-overlay">
        <div className="shrink-0 border-b px-5 py-3">
          <h3 className="text-lg font-semibold text-fg">
            {esEdicion ? "Editar número" : "Agregar número de acreditación"}
          </h3>
          <p className="mt-0.5 text-sm text-fg-muted">
            Se imprime en el Certificado de Conformidad Único, en la columna del organismo.
          </p>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-5 py-4">
          {esEdicion && usos > 0 && (
            <div className="rounded border border-estado-warn/25 bg-estado-warn-bg p-2.5 text-sm text-estado-warn">
              Este número se usó en <span className="font-medium">{usos} certificado(s)</span> ya
              emitido(s). Esos documentos conservan el número con el que salieron; la corrección
              aplica sólo a los certificados que se emitan de aquí en adelante.
            </div>
          )}

          <label className="block text-sm">
            <span className="font-medium text-fg-muted">Organismo *</span>
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value)}
              className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
            >
              <option value="">Selecciona…</option>
              {TIPOS_ACREDITACION.map((t) => (
                <option key={t.valor} value={t.valor}>{t.etiqueta}</option>
              ))}
            </select>
          </label>

          <label className="block text-sm">
            <span className="font-medium text-fg-muted">Número *</span>
            <input
              value={identificador}
              onChange={(e) => setIdentificador(e.target.value)}
              maxLength={100}
              placeholder="EE-0301-015/11"
              className="mt-1 w-full rounded border px-2 py-1.5 font-mono text-sm"
            />
          </label>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="font-medium text-fg-muted">Fecha de emisión</span>
              <input
                type="date"
                value={fechaEmision}
                onChange={(e) => setFechaEmision(e.target.value)}
                className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-fg-muted">Vigencia hasta</span>
              <input
                type="date"
                value={vigenciaFin}
                onChange={(e) => setVigenciaFin(e.target.value)}
                className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
              />
            </label>
          </div>

          <label className="block text-sm">
            <span className="font-medium text-fg-muted">Estatus</span>
            <select
              value={estatus}
              onChange={(e) => setEstatus(e.target.value)}
              className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
            >
              <option value="VIGENTE">VIGENTE</option>
              <option value="VENCIDA">VENCIDA</option>
              <option value="REVOCADA">REVOCADA</option>
            </select>
          </label>

          <label className="block text-sm">
            <span className="font-medium text-fg-muted">Notas</span>
            <textarea
              rows={2}
              value={notas}
              onChange={(e) => setNotas(e.target.value)}
              className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
            />
          </label>

          <label className="block text-sm">
            <span className="font-medium text-fg-muted">
              Motivo del cambio{esEdicion ? " *" : ""}
            </span>
            <input
              value={motivoCambio}
              onChange={(e) => setMotivoCambio(e.target.value)}
              maxLength={500}
              placeholder="Queda en la bitácora"
              className="mt-1 w-full rounded border px-2 py-1.5 text-sm"
            />
          </label>

          {error && (
            <div className="rounded border border-estado-err/25 bg-estado-err-bg p-2 text-sm text-estado-err">
              {error}
            </div>
          )}
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t bg-surface-2 px-5 py-3">
          <button
            onClick={onClose}
            className="cursor-pointer rounded border bg-surface px-3 py-1.5 text-sm transition hover:bg-surface-2"
          >
            Cancelar
          </button>
          <button
            disabled={
              !tipo ||
              !identificador.trim() ||
              (esEdicion && !motivoCambio.trim()) ||
              guardar.isPending
            }
            onClick={() => { setError(null); guardar.mutate(); }}
            className="cursor-pointer rounded bg-primary px-3 py-1.5 text-sm font-medium text-white transition hover:bg-primary-hover disabled:opacity-50"
          >
            {esEdicion ? "Guardar cambios" : "Agregar"}
          </button>
        </div>
      </div>
    </div>
  );
}
